/**
 * Parameter Audit Utility
 * Ensures ALL 503 parameters from parameters.md are properly configured
 *
 * This array contains exactly 503 parameters matching parameters.md as the single source of truth.
 * Any discrepancies between this array and parameters.md must be resolved to maintain strict alignment.
 */

// Extract all parameters from parameters.md
const PARAMETERS_MD_PARAMETERS = [
  // Vitals (15 parameters)
  'vitals_temp_c', 'vitals_sbp', 'vitals_dbp', 'vitals_rhr', 'vitals_rr', 'vitals_map', 'vitals_pp', 
  'vitals_spo2', 'vitals_sirs', 'vitals_hrv', 'vitals_vo2max', 'vitals_pain', 'vitals_weight', 
  'vitals_height', 'vitals_bmi',

  // Biometrics - Cognition (13 parameters)
  'biometrics_cognition_tmtA_time', 'biometrics_cognition_tmtA_errors', 'biometrics_cognition_tmtB_time',
  'biometrics_cognition_tmtB_errors', 'biometrics_cognition_wordRecall_immediate1', 'biometrics_cognition_wordRecall_immediate2',
  'biometrics_cognition_wordRecall_delayed', 'biometrics_cognition_stroop_word_time', 'biometrics_cognition_stroop_word_errors',
  'biometrics_cognition_stroop_color_time', 'biometrics_cognition_stroop_color_errors', 'biometrics_cognition_stroop_interference_time',
  'biometrics_cognition_stroop_interference_errors', 'biometrics_cognition_stroop_incongruent_time', 'biometrics_cognition_stroop_incongruent_errors',

  // Biometrics - Vision (8 parameters)
  'biometrics_vision_visualAcuity_distance_right', 'biometrics_vision_visualAcuity_distance_left', 'biometrics_vision_visualAcuity_distance_both',
  'biometrics_vision_visualAcuity_near_right', 'biometrics_vision_visualAcuity_near_left', 'biometrics_vision_visualAcuity_near_both',
  'biometrics_vision_visualAcuity_pinhole_right', 'biometrics_vision_visualAcuity_pinhole_left',

  // Biometrics - Pulmonary (9 parameters)
  'biometrics_pulmonary_fev1', 'biometrics_pulmonary_fvc', 'biometrics_pulmonary_pefr', 'biometrics_pulmonary_fev1_predicted',
  'biometrics_pulmonary_fef25_75', 'biometrics_pulmonary_fev1_fvc_ratio', 'biometrics_pulmonary_fvc_predicted',
  'biometrics_pulmonary_pefr_predicted', 'biometrics_pulmonary_lung_age_estimate',

  // Biometrics - Biomeasurement (15 parameters - after removing duplicates)
  'biometrics_biomeasurement_waistCircumference', 'biometrics_biomeasurement_hipCircumference', 'biometrics_biomeasurement_neckCircumference',
  'biometrics_biomeasurement_weight_inbody', 'biometrics_biomeasurement_skeletalMuscleMass', 'biometrics_biomeasurement_bodyFatMass',
  'biometrics_biomeasurement_percentBodyFat', 'biometrics_biomeasurement_waistHipRatio', 'biometrics_biomeasurement_visceralFatLevel',
  'biometrics_biomeasurement_softLeanMass', 'biometrics_biomeasurement_upperLowerBodyBalance', 'biometrics_biomeasurement_inbodyScore',
  'biometrics_biomeasurement_bodyTypeCoordinate', 'biometrics_biomeasurement_ree', 'biometrics_biomeasurement_tee',

  // Biometrics - Flexibility (3 parameters)
  'biometrics_flexibility_armReach_back_right', 'biometrics_flexibility_armReach_back_left', 'biometrics_flexibility_legReach_sitting',

  // Biometrics - Balance (24 parameters)
  'biometrics_balance_ybt_right_anterior_1', 'biometrics_balance_ybt_left_anterior_1', 'biometrics_balance_ybt_right_posteromedial_1',
  'biometrics_balance_ybt_left_posteromedial_1', 'biometrics_balance_ybt_right_posterolateral_1', 'biometrics_balance_ybt_left_posterolateral_1',
  'biometrics_balance_ybt_right_anterior_2', 'biometrics_balance_ybt_left_anterior_2', 'biometrics_balance_ybt_right_posteromedial_2',
  'biometrics_balance_ybt_left_posteromedial_2', 'biometrics_balance_ybt_right_posterolateral_2', 'biometrics_balance_ybt_left_posterolateral_2',
  'biometrics_balance_ybt_right_anterior_avg', 'biometrics_balance_ybt_left_anterior_avg', 'biometrics_balance_ybt_right_posteromedial_avg',
  'biometrics_balance_ybt_left_posteromedial_avg', 'biometrics_balance_ybt_right_posterolateral_avg', 'biometrics_balance_ybt_left_posterolateral_avg',
  'biometrics_balance_ybt_right_composite_reach', 'biometrics_balance_ybt_left_composite_reach', 'biometrics_balance_ybt_composite_deviation',
  'biometrics_balance_ybt_anterior_deviation', 'biometrics_balance_ybt_posteromedial_deviation', 'biometrics_balance_ybt_posterolateral_deviation',
  'biometrics_balance_ybt_performance_ratio',

  // Biometrics - Strength (10 parameters)
  'biometrics_strength_handgrip_right_1', 'biometrics_strength_handgrip_right_2', 'biometrics_strength_handgrip_left_1',
  'biometrics_strength_handgrip_left_2', 'biometrics_strength_handgrip_right_avg', 'biometrics_strength_handgrip_left_avg',
  'biometrics_strength_handgrip_normative_status', 'biometrics_strength_handgrip_deviation_percent', 'biometrics_strength_handgrip_relative_strength',
  'biometrics_strength_pushups_completed',

  // Biometrics - Endurance (6 parameters)
  'biometrics_endurance_wallsit_duration', 'biometrics_endurance_pushups_completed', 'biometrics_endurance_step_test_duration',
  'biometrics_endurance_heart_rate_peak', 'biometrics_endurance_heart_rate_1min', 'biometrics_endurance_heart_rate_1min_post_exercise',

  // Biometrics - Power (5 parameters)
  'biometrics_power_jump_best', 'biometrics_power_jump_percent_max', 'biometrics_power_jump_average_height',
  'biometrics_power_landing_impact', 'biometrics_power_power_watts',

  // Physical Exam - Integumentary (12 parameters)
  'pe_integumentary_alopecia', 'pe_integumentary_scalp_lesions', 'pe_integumentary_arm_lesions', 'pe_integumentary_leg_lesions',
  'pe_integumentary_finger_nails', 'pe_integumentary_toe_nails', 'pe_integumentary_skin_turgor', 'pe_integumentary_jaundice',
  'pe_integumentary_pallor', 'pe_integumentary_bruising', 'pe_integumentary_surgical_scar', 'pe_integumentary_caput_medusa',

  // Physical Exam - Lymph Nodes (8 parameters)
  'pe_lymph_occipital_nodes', 'pe_lymph_periauricular_nodes', 'pe_lymph_cervical_nodes', 'pe_lymph_submental_nodes',
  'pe_lymph_submandibular_nodes', 'pe_lymph_supraclavicular_nodes', 'pe_lymph_femoral_nodes', 'pe_lymph_popliteal_nodes',

  // Physical Exam - HEENT Eyes (8 parameters)
  'pe_heent_eyes_conjunctiva', 'pe_heent_eyes_sclera', 'pe_heent_eyes_ocular_shape', 'pe_heent_eyes_vision_field',
  'pe_heent_eyes_light_reflex', 'pe_heent_eyes_red_reflex', 'pe_heent_eyes_h_test', 'pe_heent_eyes_finger_to_nose',

  // Physical Exam - HEENT Ears (5 parameters)
  'pe_heent_ears_external_ear_canal', 'pe_heent_ears_tympanic_membrane', 'pe_heent_ears_whisper_test',
  'pe_heent_ears_rinne_test', 'pe_heent_ears_weber_test',

  // Physical Exam - HEENT Nose/Sinus (5 parameters)
  'pe_heent_nose_nasal_turbinate', 'pe_heent_nose_nasal_polyp', 'pe_heent_nose_nasal_septum_deviation',
  'pe_heent_nose_maxillary_sinus', 'pe_heent_nose_frontal_sinus',

  // Physical Exam - HEENT Mouth/Throat (11 parameters)
  'pe_heent_mouth_oral_mucosa', 'pe_heent_mouth_san_mieng', 'pe_heent_mouth_vom_mieng', 'pe_heent_mouth_luoi',
  'pe_heent_mouth_amidan', 'pe_heent_mouth_mallampati', 'pe_heent_mouth_uvula_position', 'pe_heent_mouth_tmj_tenderness',
  'pe_heent_mouth_maxillary_teeth', 'pe_heent_mouth_mandibular_teeth', 'pe_heent_mouth_cough_reflex',

  // Physical Exam - Neck (1 parameter)
  'pe_neck_thyroid',

  // Physical Exam - Chest/Respiratory (6 parameters)
  'pe_chest_resp_chest_expansion_symmetry', 'pe_chest_resp_chest_expansion_range', 'pe_chest_resp_crackles',
  'pe_chest_resp_wheezing', 'pe_chest_resp_rhonchi', 'pe_chest_resp_stridor',

  // Physical Exam - Cardiovascular (18 parameters)
  'pe_cardio_heart_rhythm', 'pe_cardio_s1', 'pe_cardio_s2', 'pe_cardio_s3', 'pe_cardio_s4', 'pe_cardio_systolic_murmur',
  'pe_cardio_diastolic_murmur', 'pe_cardio_apical_impulse', 'pe_cardio_carotid_bruit', 'pe_cardio_hepatojugular_reflux',
  'pe_cardio_radial_pulse', 'pe_cardio_cap_refill_finger', 'pe_cardio_abdominal_bruit', 'pe_cardio_posterior_tibial',
  'pe_cardio_dorsalis_pedis', 'pe_cardio_pedal_edema', 'pe_cardio_cap_refill_toe', 'pe_cardio_varicose_veins',

  // Physical Exam - Abdomen (11 parameters)
  'pe_abdomen_bowel_sound', 'pe_abdomen_abdominal_distension', 'pe_abdomen_hepatomegaly', 'pe_abdomen_splenomegaly',
  'pe_abdomen_epigastric_tenderness', 'pe_abdomen_luq_tenderness', 'pe_abdomen_ruq_tenderness', 'pe_abdomen_llq_tenderness',
  'pe_abdomen_rlq_tenderness', 'pe_abdomen_suprapubic_tenderness', 'pe_abdomen_cva_tenderness',

  // Physical Exam - Musculoskeletal (19 parameters)
  'pe_msk_cervical_lordosis', 'pe_msk_thoracic_kyphosis', 'pe_msk_lumbar_lordosis', 'pe_msk_visible_scoliosis',
  'pe_msk_cervical_tenderness', 'pe_msk_thoracic_tenderness', 'pe_msk_lumbar_tenderness', 'pe_msk_cervical_flexion',
  'pe_msk_cervical_extension', 'pe_msk_cervical_rotation', 'pe_msk_lumbar_flexion', 'pe_msk_lumbar_extension',
  'pe_msk_shoulder_height_symmetry', 'pe_msk_shoulder_abduction_arom', 'pe_msk_hip_height_symmetry', 'pe_msk_thomas_hip_test',
  'pe_msk_faber_test', 'pe_msk_knee_alignment', 'pe_msk_patellar_reflex',

  // Physical Exam - Neurological (6 parameters)
  'pe_neuro_ulnar_nerve_sensory', 'pe_neuro_radial_nerve_sensory', 'pe_neuro_medial_nerve_sensory',
  'pe_neuro_pinprick_sensory', 'pe_neuro_tandem_walk', 'pe_neuro_romberg',

  // Imaging - Spleen US (6 parameters)
  'imaging_spleen_us_craniocaudal_length', 'imaging_spleen_us_capsular_edge_nodularity', 'imaging_spleen_us_parenchymal_echogenicity',
  'imaging_spleen_us_largest_lesion_diameter', 'imaging_spleen_us_splenic_vein_diameter', 'imaging_spleen_us_psdr',

  // Imaging - Liver US (7 parameters)
  'imaging_liver_us_craniocaudal_length', 'imaging_liver_us_capsular_edge_nodularity', 'imaging_liver_us_echogenicity_steatosis',
  'imaging_liver_us_portal_vein_diameter', 'imaging_liver_us_lirads', 'imaging_liver_us_largest_lesion_diameter',
  'imaging_liver_us_hepatic_vein_waveform',

  // Imaging - Carotid US (18 parameters)
  'imaging_carotid_us_l_cca_score', 'imaging_carotid_us_r_cca_score', 'imaging_carotid_us_l_bulb_score',
  'imaging_carotid_us_r_bulb_score', 'imaging_carotid_us_l_ica_score', 'imaging_carotid_us_r_ica_score',
  'imaging_carotid_us_l_cimt', 'imaging_carotid_us_r_cimt', 'imaging_carotid_us_plaque_presence',
  'imaging_carotid_us_surface_ulceration', 'imaging_carotid_us_calcification_pattern', 'imaging_carotid_us_l_ica_psv',
  'imaging_carotid_us_r_ica_psv', 'imaging_carotid_us_l_ica_edv', 'imaging_carotid_us_r_ica_edv',
  'imaging_carotid_us_vascular_age_imt', 'imaging_carotid_us_vascular_age_cac',

  // Imaging - EKG (10 parameters)
  'imaging_ekg_rhythm_abnormality', 'imaging_ekg_axis_deviation', 'imaging_ekg_ventricular_hypertrophy',
  'imaging_ekg_conduction_abnormality', 'imaging_ekg_pr_interval', 'imaging_ekg_qrs_duration',
  'imaging_ekg_qtc_interval', 'imaging_ekg_st_changes', 'imaging_ekg_t_wave_interval', 'imaging_ekg_ectopy',

  // Imaging - Bladder US (8 parameters)
  'imaging_bladder_us_pre_void_volume', 'imaging_bladder_us_pvr_volume', 'imaging_bladder_us_wall_thickness',
  'imaging_bladder_us_calculi_presence', 'imaging_bladder_us_calculi_description', 'imaging_bladder_us_masses_presence',
  'imaging_bladder_us_masses_description', 'imaging_bladder_us_ureteral_jets',

  // Imaging - Chest XR (14 parameters)
  'imaging_chest_xr_date', 'imaging_chest_xr_pleural_effusion', 'imaging_chest_xr_enlarged_pulmonary_artery',
  'imaging_chest_xr_enlarged_right_ventricle', 'imaging_chest_xr_flattened_diaphragm', 'imaging_chest_xr_cardiothoracic_ratio_gt_50',
  'imaging_chest_xr_cavitary_lung_lesion', 'imaging_chest_xr_widened_mediastinum', 'imaging_chest_xr_enlarged_hilar_lymph_nodes',
  'imaging_chest_xr_atelectasis', 'imaging_chest_xr_pulmonary_infiltrates', 'imaging_chest_xr_pulmonary_nodule_or_mass',
  'imaging_chest_xr_spinal_deviation', 'imaging_chest_xr_lytic_bone_lesion',

  // Imaging - Heart US (5 parameters)
  'imaging_heart_us_ejection_fraction', 'imaging_heart_us_wall_motion', 'imaging_heart_us_valve_function',
  'imaging_heart_us_chamber_size', 'imaging_heart_us_pericardial_effusion',

  // Imaging - Gallbladder US (5 parameters)
  'imaging_gallbladder_us_wall_thickness', 'imaging_gallbladder_us_stones_present', 'imaging_gallbladder_us_sludge_present',
  'imaging_gallbladder_us_polyps_present', 'imaging_gallbladder_us_distension',

  // Imaging - Kidney US (6 parameters)
  'imaging_kidney_us_right_size', 'imaging_kidney_us_left_size', 'imaging_kidney_us_echogenicity',
  'imaging_kidney_us_hydronephrosis', 'imaging_kidney_us_stones_present', 'imaging_kidney_us_masses_present',

  // Imaging - Thyroid US (12 parameters)
  'imaging_thyroid_us_date', 'imaging_thyroid_us_right_lobe_size', 'imaging_thyroid_us_left_lobe_size',
  'imaging_thyroid_us_isthmus_thickness', 'imaging_thyroid_us_echogenicity', 'imaging_thyroid_us_nodules_present',
  'imaging_thyroid_us_largest_nodule_size', 'imaging_thyroid_us_nodule_composition', 'imaging_thyroid_us_calcifications',
  'imaging_thyroid_us_vascularity', 'imaging_thyroid_us_lymph_nodes', 'imaging_thyroid_us_tirads_score',

  // Imaging - Other Imaging (12 parameters)
  'imaging_other_study_type', 'imaging_other_study_date', 'imaging_other_modality', 'imaging_other_body_region',
  'imaging_other_findings', 'imaging_other_measurements', 'imaging_other_interpretation', 'imaging_other_impression',
  'imaging_other_recommendations', 'imaging_other_comparison', 'imaging_other_technique', 'imaging_other_contrast',

  // Imaging - Consolidated Findings (9 parameters)
  'imaging_carotid_us_all_findings', 'imaging_ekg_all_findings', 'imaging_bladder_us_all_findings',
  'imaging_chest_xr_all_findings', 'imaging_heart_us_all_findings', 'imaging_gallbladder_us_all_findings',
  'imaging_kidney_us_all_findings', 'imaging_thyroid_us_all_findings', 'imaging_other_imaging_all_findings',

  // Labs - Cardiac (3 parameters)
  'labs_cardiac_troponin', 'labs_cardiac_bnp', 'labs_cardiac_ckmb',

  // Labs - CBC (23 parameters)
  'labs_cbc_wbc', 'labs_cbc_neut', 'labs_cbc_lymph', 'labs_cbc_mono', 'labs_cbc_eos', 'labs_cbc_baso',
  'labs_cbc_neut_pct', 'labs_cbc_lymph_pct', 'labs_cbc_mono_pct', 'labs_cbc_eos_pct', 'labs_cbc_baso_pct',
  'labs_cbc_rbc', 'labs_cbc_hgb', 'labs_cbc_hct', 'labs_cbc_mcv', 'labs_cbc_mch', 'labs_cbc_mchc',
  'labs_cbc_rdw', 'labs_cbc_rdw_sd', 'labs_cbc_plt', 'labs_cbc_mpv', 'labs_cbc_pdw', 'labs_cbc_pct',
  'labs_cbc_p_lcr', 'labs_cbc_nlr',

  // Labs - Coagulation (6 parameters)
  'labs_coagulation_pt', 'labs_coagulation_inr', 'labs_coagulation_ptt', 'labs_coagulation_thrombin_time',
  'labs_coagulation_fibrinogen', 'labs_coagulation_d_dimer',

  // Labs - Glucose (2 parameters)
  'labs_glucose_hba1c', 'labs_glucose_glucose',

  // Labs - Lipid (11 parameters)
  'labs_lipid_apob', 'labs_lipid_chol_total', 'labs_lipid_hdl', 'labs_lipid_ldl', 'labs_lipid_trig',
  'labs_lipid_vldl', 'labs_lipid_non_hdl', 'labs_lipid_tg_hdl_ratio', 'labs_lipid_tc_glu_ratio',
  'labs_lipid_tc_hdl_ratio', 'labs_lipid_athero_idx',

  // Labs - Electrolytes (9 parameters)
  'labs_electrolytes_magnesium', 'labs_electrolytes_phosphorous', 'labs_electrolytes_sodium', 'labs_electrolytes_potassium',
  'labs_electrolytes_chloride', 'labs_electrolytes_calcium', 'labs_electrolytes_bicarb', 'labs_electrolytes_osmolality',
  'labs_electrolytes_na_k_ratio',

  // Labs - Vitamins (5 parameters)
  'labs_vitamins_iron', 'labs_vitamins_b12', 'labs_vitamins_d3', 'labs_vitamins_b9', 'labs_vitamins_ferritin',

  // Labs - Renal (4 parameters)
  'labs_renal_creatinine', 'labs_renal_egfr', 'labs_renal_urea', 'labs_renal_bun',

  // Labs - Inflammation (3 parameters)
  'labs_inflammation_uric_acid', 'labs_inflammation_hscrp', 'labs_inflammation_esr',

  // Labs - Hepatology (7 parameters)
  'labs_hepatology_ast', 'labs_hepatology_alt', 'labs_hepatology_ggt', 'labs_hepatology_bili_total',
  'labs_hepatology_bili_direct', 'labs_hepatology_bili_indirect', 'labs_hepatology_alp',

  // Labs - Protein (4 parameters)
  'labs_protein_total', 'labs_protein_albumin', 'labs_protein_prealbumin', 'labs_protein_alb_glob_ratio',

  // Labs - Thyroid (3 parameters)
  'labs_thyroid_ft4', 'labs_thyroid_tsh', 'labs_thyroid_ft3',

  // Labs - Urine (14 parameters)
  'labs_urine_sg', 'labs_urine_ph', 'labs_urine_nitrite', 'labs_urine_protein', 'labs_urine_glucose',
  'labs_urine_ketones', 'labs_urine_urobil', 'labs_urine_bili', 'labs_urine_ery', 'labs_urine_leuk',
  'labs_urine_microalb', 'labs_urine_creat', 'labs_urine_uacr',

  // Lifestyle - Nutrition (30 parameters)
  'lifestyle_nutrition_protein_intake', 'lifestyle_nutrition_fruit_intake', 'lifestyle_nutrition_vegetables_intake',
  'lifestyle_nutrition_refined_carbs_intake', 'lifestyle_nutrition_fiber_intake', 'lifestyle_nutrition_seeds_nuts_intake',
  'lifestyle_nutrition_sodium_intake', 'lifestyle_nutrition_sugar_intake', 'lifestyle_nutrition_fried_food_intake',
  'lifestyle_nutrition_healthy_oil_use', 'lifestyle_nutrition_ultraprocessed_intake', 'lifestyle_nutrition_water_intake',
  'lifestyle_nutrition_alcohol_intake', 'lifestyle_nutrition_weight_loss_concern', 'lifestyle_nutrition_weight_gain_concern',
  'lifestyle_nutrition_appetite_lower_normal', 'lifestyle_nutrition_appetite_higher_normal', 'lifestyle_nutrition_skip_meals_hungry',
  'lifestyle_nutrition_full_small_amount', 'lifestyle_nutrition_difficulty_chewing_swallowing', 'lifestyle_nutrition_problems_taste_smell',
  'lifestyle_nutrition_worry_food_access', 'lifestyle_nutrition_bored_eating', 'lifestyle_nutrition_busy_eating',
  'lifestyle_nutrition_late_eating', 'lifestyle_nutrition_binge_eating', 'lifestyle_nutrition_guilty_eating',
  'lifestyle_nutrition_rushed_eating', 'lifestyle_nutrition_distracted_eating', 'lifestyle_nutrition_fad_diets',

  // Lifestyle - Movement (14 parameters)
  'lifestyle_movement_moderate_activity_days', 'lifestyle_movement_vigorous_activity_days', 'lifestyle_movement_strength_training_days',
  'lifestyle_movement_flexibility_stretching_days', 'lifestyle_movement_balance_practice_days', 'lifestyle_movement_incidental_walking_time',
  'lifestyle_movement_sedentary_hours', 'lifestyle_movement_sitting_break_days', 'lifestyle_movement_red_flag_sob',
  'lifestyle_movement_red_flag_chest_pain', 'lifestyle_movement_red_flag_palpitations', 'lifestyle_movement_red_flag_dizziness',
  'lifestyle_movement_red_flag_pain', 'lifestyle_movement_red_flag_imbalance',

  // Lifestyle - Recovery (20 parameters)
  'lifestyle_recovery_sleep_quality', 'lifestyle_recovery_sleep_duration', 'lifestyle_recovery_sleep_continuity',
  'lifestyle_recovery_sleep_onset', 'lifestyle_recovery_bedtime_consistency', 'lifestyle_recovery_late_bedtime',
  'lifestyle_recovery_sleep_environment_factors_uncomfortable_mattress', 'lifestyle_recovery_sleep_environment_factors_room_temperature',
  'lifestyle_recovery_sleep_environment_factors_bright_light', 'lifestyle_recovery_sleep_environment_factors_loud_noises',
  'lifestyle_recovery_sleep_environment_factors_electronic_screens', 'lifestyle_recovery_sleep_environment_factors_caffeine_stimulants',
  'lifestyle_recovery_napping_frequency', 'lifestyle_recovery_daytime_sleepiness', 'lifestyle_recovery_sleep_disorders_waking_drenched_sweats',
  'lifestyle_recovery_sleep_disorders_teeth_grinding', 'lifestyle_recovery_sleep_disorders_nasal_congestion_snoring',
  'lifestyle_recovery_sleep_disorders_restless_legs', 'lifestyle_recovery_sleep_disorders_racing_thoughts',
  'lifestyle_recovery_sleep_disorders_upsetting_dreams',

  // Lifestyle - Emotion (22 parameters)
  'lifestyle_emotion_little_interest', 'lifestyle_emotion_feeling_down', 'lifestyle_emotion_feeling_nervous',
  'lifestyle_emotion_not_being_able', 'lifestyle_emotion_difficulty_concentrating', 'lifestyle_emotion_feeling_irritable',
  'lifestyle_emotion_felt_unable_control', 'lifestyle_emotion_felt_confident_problems', 'lifestyle_emotion_felt_things_going_way',
  'lifestyle_emotion_felt_difficulties_piling_up', 'lifestyle_emotion_lack_companionship', 'lifestyle_emotion_feel_left_out',
  'lifestyle_emotion_feel_isolated', 'lifestyle_emotion_burnout_emotionally_exhausted', 'lifestyle_emotion_burnout_cynical',
  'lifestyle_emotion_burnout_cant_disconnect', 'lifestyle_emotion_burnout_no_breaks', 'lifestyle_emotion_burnout_long_hours',
  'lifestyle_emotion_burnout_personal_time', 'lifestyle_emotion_burnout_family_time', 'lifestyle_emotion_burnout_purpose',

  // Lifestyle - Environment (5 parameters)
  'lifestyle_environment_smoking_quantity_daily', 'lifestyle_environment_smoking_duration_years',
  'lifestyle_environment_outdoor_daylight_minutes', 'lifestyle_environment_nature_time_days_weekly',
  'lifestyle_environment_morning_outdoor_light_days_weekly'
];

/**
 * Audits parameter configuration completeness
 * @param {Array} configuredParameters - Parameters found in configuration files
 * @returns {Object} Audit results
 */
export const auditParameterConfiguration = (configuredParameters) => {
  const configuredSet = new Set(configuredParameters);
  const expectedSet = new Set(PARAMETERS_MD_PARAMETERS);
  
  const missingFromConfig = PARAMETERS_MD_PARAMETERS.filter(param => !configuredSet.has(param));
  const extraInConfig = configuredParameters.filter(param => !expectedSet.has(param));
  
  return {
    totalExpected: PARAMETERS_MD_PARAMETERS.length,
    totalConfigured: configuredParameters.length,
    missingFromConfig,
    extraInConfig,
    missingCount: missingFromConfig.length,
    extraCount: extraInConfig.length,
    configurationComplete: missingFromConfig.length === 0,
    parametersByCategory: categorizeParameters(PARAMETERS_MD_PARAMETERS),
    missingByCategory: categorizeParameters(missingFromConfig)
  };
};

/**
 * Categorizes parameters by their prefix
 */
const categorizeParameters = (parameters) => {
  const categories = {};
  
  parameters.forEach(param => {
    const parts = param.split('_');
    const category = parts[0];
    const subcategory = parts[1] || 'general';
    
    if (!categories[category]) categories[category] = {};
    if (!categories[category][subcategory]) categories[category][subcategory] = [];
    
    categories[category][subcategory].push(param);
  });
  
  return categories;
};

export { PARAMETERS_MD_PARAMETERS };
